import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import { AdvanceFilter, FilterBody } from "@cleanup/components/AdvancedFilters/FilterStyle";
import GeneralFooterFilter from "@cleanup/components/GeneralFooterFilter/GeneralFooterFilter";
import { Button, Container, Form } from "react-bootstrap";
import CheckboxItem from "@cleanup/components/AdvancedFilters/CheckboxItem";
import SntArrowDownIcon from "@icons/SntArrowDownIcon";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

interface SearchField {
  _idx: string;
  label: string;
  checked?: boolean;
  key?: string;
}

interface CategoryDescriptor {
  categoryID: string;
  categoryLabel: string;
  searchFields: SearchField[];
}

interface UnselectedGroup {
  id: string;
  name: string;
  group: SearchField[];
}

interface MoreFilterProps {
  descriptors: CategoryDescriptor[];
  data?: string[];
  onChange?: (data: string[]) => void;
  disabled?: boolean;
}

const MoreFilter: React.FC<MoreFilterProps> = ({
  descriptors,
  data = [],
  onChange,
  disabled = false
}) => {
  const [isShow, setShow] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SearchField[]>([]);
  const [unSelectedItems, setUnSelectedItems] = useState<UnselectedGroup[]>([]);
  const [searchText, setSearchText] = useState("");
  const [keyUpDelay, setKeyUpDelay] = useState<NodeJS.Timeout | null>(null);
  const tempSelected = useRef<string[]>([]);
  const tempUnSelected = useRef<string[]>([]);


  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();

  const devideArray = (searchText = "") => {
    const _ctempSelected: string[] = [];
    unSelectedItems.forEach((items) => {
      items.group.forEach((item) => {
        if (item.checked && tempSelected.current.indexOf(item._idx) === -1) {
          _ctempSelected.push(item._idx);
        }
      });
    });

    const _tempSelected = [...tempSelected.current, ..._ctempSelected];
    const _tempUnSelected = [
      ...tempUnSelected.current,
      ...selectedItems
        .filter(
          (item) =>
            !item.checked && tempUnSelected.current.indexOf(item._idx) === -1
        )
        .map((i) => i._idx),
    ];
    const selected: SearchField[] = [];
    const unselected: UnselectedGroup[] = [];

    for (let i = 0; i < descriptors.length; i++) {
      const category = descriptors[i];
      const group: SearchField[] = [];

      for (let i = 0; i < category.searchFields.length; i++) {
        if (data.indexOf(category.searchFields[i]["_idx"]) === -1) {
          category.searchFields[i].checked =
            _tempSelected.indexOf(category.searchFields[i]["_idx"]) !== -1;

          if (
            !searchText.trim() ||
            (searchText &&
              category.searchFields[i].label &&
              category.searchFields[i].label
                .toLowerCase()
                .indexOf(searchText.trim().toLowerCase()) !== -1)
          ) {
            group.push(category.searchFields[i]);
          }
        } else {
          category.searchFields[i].checked =
            _tempUnSelected.indexOf(category.searchFields[i]["_idx"]) === -1;
          selected.push(category.searchFields[i]);
        }
      }

      if (group.length > 0) {
        unselected.push({
          id: category.categoryID,
          name: category.categoryLabel,
          group: group,
        });
      }
    }

    setSelectedItems(selected);
    setUnSelectedItems(unselected);
  };

  useEffect(() => {
    devideArray();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptors, data]);

  const getData = useCallback(
    (tempSelected: string[]) => {
      let dataResult = selectedItems
        .filter((s) => s.checked === true)
        .map((s) => s._idx);

      for (let i = 0; i < unSelectedItems.length; i++) {
        dataResult = [
          ...dataResult,
          ...unSelectedItems[i].group
            .filter((s) => s.checked === true)
            .map((s) => s._idx),
        ];
      }

      dataResult = [...dataResult, ...tempSelected];
      return Array.from(new Set(dataResult));
    },
    [unSelectedItems, selectedItems]
  );

  const clearTemp = () => {
    tempSelected.current = [];
    tempUnSelected.current = [];
  };

  const submitChange = () => {
    const result = getData(tempSelected.current).sort();
    const sortedData = Array.from(data).sort();
    if (JSON.stringify(result) !== JSON.stringify(sortedData)) {
      if (onChange) {
        onChange(result);
      }
    }
    clearTemp();
  };

  const refWrapper = useOuterClick(() => {
    if (isShow) {
      setShow(false);
      submitChange();
    }
  });

  const onClickButton = () => {
    if (isShow) {
      setShow(false);
      submitChange();
    } else {
      setSearchText("");
      popupStyleRef.current = getPosition();
      setShow(true);

      if (keyUpDelay) {
        clearTimeout(keyUpDelay);
      }
      setKeyUpDelay(
        setTimeout(() => {
          devideArray("");
        }, 100)
      );
    }
  };

  const onAppliedFilter = () => {
    setShow(false);
    submitChange();
  };

  const onClearFilter = () => {
    setShow(false);
    if (JSON.stringify(getData(tempSelected.current)) !== JSON.stringify([])) {
      if (onChange) {
        onChange([]);
      }
    }
  };

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);

    const text = event.target.value;

    if (keyUpDelay) {
      clearTimeout(keyUpDelay);
    }

    setKeyUpDelay(
      setTimeout(() => {
        devideArray(text);
      }, 500)
    );
  };

  const checkbox = (
    searchField: SearchField,
    setData: () => void,
    key: number | string,
    resetTemp: (field: SearchField) => void
  ) => {
    return (
      <CheckboxItem
        key={key}
        checked={searchField.checked || false}
        onChange={(value) => {
          searchField.checked = value;
          resetTemp(searchField);
          setData();
        }}
        label={searchField.label}
      />
    );
  };

  const unSelectedCategoriesRender = useMemo(() => {
    return unSelectedItems.map((searchField, index) => {
      const group = searchField.group.map((searchField, i) => {
        return checkbox(
          searchField,
          () => {
            setUnSelectedItems([...unSelectedItems]);
          },
          i,
          (field) => {
            if (!field.checked) {
              tempSelected.current.splice(
                tempSelected.current.indexOf(field._idx),
                1
              );
            }
            if (
              field.checked &&
              tempSelected.current.indexOf(field._idx) === -1
            ) {
              tempSelected.current.push(field._idx);
            }
          }
        );
      });
      return (
        <div key={index}>
          <Container className="mb-3">
            <div className="fw-bold mb-2">{searchField.name}</div>
            {group}
          </Container>
          <div className="mb-3 border-top border-sensolus-grey"></div>
        </div>
      );
    });
  }, [unSelectedItems]);

  const selectedCategoriesRender = useMemo(() => {
    return selectedItems.map((searchField, index) => {
      return checkbox(
        searchField,
        () => {
          setSelectedItems([...selectedItems]);
        },
        index,
        (field) => {
          if (field.checked) {
            tempUnSelected.current.splice(
              tempUnSelected.current.indexOf(field._idx),
              1
            );
          }
          if (
            !field.checked &&
            tempUnSelected.current.indexOf(field._idx) === -1
          ) {
            tempUnSelected.current.push(field._idx);
          }
        }
      );
    });
  }, [selectedItems]);

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={translate(LanguageKey.all_filter_description_key)}
        disabled={disabled}
        onClick={onClickButton}
      >
        {translate(LanguageKey.filters_key)}
        <SntArrowDownIcon />
      </Button>

      <FilterBody
        ref={popupRef}
        style={{ display: isShow ? "block" : "none", ...popupStyleRef.current }}
      >
        <Container className="mb-3 mt-3">
          <Form.Control
            name="search"
            type="text"
            value={searchText}
            onChange={onChangeInput}
          />
        </Container>
        <div style={{ overflowY: "auto", height: "300px" }}>
          <Container>{selectedCategoriesRender}</Container>
          <div className="mb-3 mt-3 border-top border-sensolus-grey"></div>
          {unSelectedCategoriesRender}
        </div>
        <GeneralFooterFilter
          onClearFilter={() => onClearFilter()}
          onAppliedFilter={() => onAppliedFilter()}
          isShowExclude={false}
        />
      </FilterBody>
    </AdvanceFilter>
  );
};

export default MoreFilter;
